import { useCallback, useEffect, useRef, useState } from 'react'
import { hourmeterService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import type { ActiveContext } from '../types'

const CACHE_PREFIX = 'active-context-'
const CACHE_TTL_MS = 5 * 60 * 1000          // 5 min — evita cache stale tras reasignaciones
const FOCUS_REFETCH_GATE_MS = 30 * 1000     // 30s — debounce focus para no martillar al backend

interface CacheEntry {
  ctx: ActiveContext
  ts: number
}

function isCacheEntry(entry: unknown): entry is CacheEntry {
  return typeof entry === 'object' && entry !== null
    && typeof (entry as CacheEntry).ts === 'number'
    && !!(entry as CacheEntry).ctx
    && typeof ((entry as CacheEntry).ctx as ActiveContext).kind === 'string'
}

export function useActiveContext() {
  const { operator } = useAuth()
  const [context, setContext] = useState<ActiveContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const lastFetchTs = useRef<number>(0)

  const cacheKey = operator?.id != null ? `${CACHE_PREFIX}${operator.id}` : null

  // Hidrata cache solo si vigente (TTL). Datos stale (residents cambiados,
  // nuevas conditions, etc) no engañan al user — se hace fetch fresh.
  useEffect(() => {
    if (!cacheKey) return
    try {
      const raw = localStorage.getItem(cacheKey)
      if (!raw) return
      const entry = JSON.parse(raw)
      if (isCacheEntry(entry) && Date.now() - entry.ts < CACHE_TTL_MS) {
        setContext(entry.ctx)
      } else {
        // Cache viejo (sin wrapper) o expirado → limpia para no parsearlo en cada hidrate.
        localStorage.removeItem(cacheKey)
      }
    } catch {
      // noop
    }
  }, [cacheKey])

  const fetchContext = useCallback(async () => {
    if (!operator?.id) return
    setLoading(true)
    setError(null)
    try {
      const ctx = await hourmeterService.getActiveContext(operator.id)
      setContext(ctx)
      lastFetchTs.current = Date.now()
      if (cacheKey) {
        const entry: CacheEntry = { ctx, ts: lastFetchTs.current }
        localStorage.setItem(cacheKey, JSON.stringify(entry))
      }
    } catch (err: any) {
      console.error('Error fetching active context:', err)
      setError(err?.response?.data?.message || 'No se pudo cargar contexto activo')
    } finally {
      setLoading(false)
    }
  }, [operator?.id, cacheKey])

  useEffect(() => {
    fetchContext()
  }, [fetchContext])

  // Refetch al recuperar foco con gate de 30s — evita stale después de pestaña
  // inactiva sin martillar el backend cuando el user alterna pestañas rápido.
  // visibilitychange cubre mejor el caso móvil (suspend/resume) que focus.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastFetchTs.current < FOCUS_REFETCH_GATE_MS) return
      fetchContext()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [fetchContext])

  return { context, loading, error, refetch: fetchContext }
}
